import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import SaveIcon from '@material-ui/icons/Save'
import AddIcon from '@material-ui/icons/Add'
import { Alert } from '@material-ui/lab'
import {
    getNotificationEmails,
    createNotificationEmail,
    updateNotificationEmail,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

// Small util — shallow-compare booleans + string to detect a row's dirty state without pulling in
// an equality lib. Enough for a four-field row and avoids "always looks dirty" false positives.
const rowsEqual = (a, b) =>
    a.email === b.email &&
    a.enabled === b.enabled &&
    a.notifyOnSelfRegister === b.notifyOnSelfRegister &&
    a.notifyOnPlanChange === b.notifyOnPlanChange &&
    a.notifyOnDsIngest === b.notifyOnDsIngest

const emptyDraft = () => ({
    email: '',
    notifyOnRegistration: false,
    notifyOnPlanChange: false,
    notifyOnDsIngest: false,
})

const NotificationEmails = () => {
    const [rows, setRows] = useState([])           // last-known-server rows
    const [drafts, setDrafts] = useState({})       // in-progress edits, keyed by email
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [newDraft, setNewDraft] = useState(emptyDraft())

    const fetch = useCallback(() => {
        setLoading(true)
        getNotificationEmails()()
            .then((res) => {
                const list = Array.isArray(res) ? res : []
                setRows(list)
                // Reset any stale drafts against fresh data.
                setDrafts({})
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load notification emails' }))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { fetch() }, [fetch])

    // A row's "current" values are drafts[email] if present, else the server-known row.
    const currentFor = (row) => drafts[row.email] ?? row

    const setDraftField = (email, field, value) => {
        setDrafts((prev) => {
            const server = rows.find((r) => r.email === email)
            const base = prev[email] ?? server
            const next = { ...base, [field]: value }
            // If the edit brings the draft back to server state, drop the draft entry (row goes clean).
            if (server && rowsEqual(next, server)) {
                const { [email]: _drop, ...rest } = prev
                return rest
            }
            return { ...prev, [email]: next }
        })
    }

    const saveRow = (email) => {
        const draft = drafts[email]
        if (!draft) return
        setSaving(true)
        updateNotificationEmail({
            email: draft.email,
            enabled: !!draft.enabled,
            // Backend DTO uses notifyOnRegistration, not notifyOnSelfRegister.
            notifyOnRegistration: !!draft.notifyOnSelfRegister,
            notifyOnPlanChange: !!draft.notifyOnPlanChange,
            notifyOnDsIngest: !!draft.notifyOnDsIngest,
        })()
            .then(() => {
                Toast({ type: 'success', message: `Saved ${email}` })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || `Failed to save ${email}`
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setSaving(false))
    }

    const addNew = () => {
        const email = newDraft.email.trim()
        if (!email) {
            Toast({ type: 'error', message: 'Email required' })
            return
        }
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            Toast({ type: 'error', message: 'Enter a valid email address' })
            return
        }
        if (rows.some((r) => r.email === email)) {
            Toast({ type: 'error', message: `${email} is already in the list — edit the existing row instead` })
            return
        }
        if (!newDraft.notifyOnRegistration && !newDraft.notifyOnPlanChange && !newDraft.notifyOnDsIngest) {
            Toast({ type: 'error', message: 'Pick at least one notification type' })
            return
        }
        setSaving(true)
        createNotificationEmail({
            email,
            notifyOnRegistration: newDraft.notifyOnRegistration,
            notifyOnPlanChange: newDraft.notifyOnPlanChange,
            notifyOnDsIngest: newDraft.notifyOnDsIngest,
        })()
            .then(() => {
                Toast({ type: 'success', message: `${email} added` })
                setNewDraft(emptyDraft())
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Failed to add email'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setSaving(false))
    }

    // Sort: enabled first, then alphabetical by email for stable positional scanning.
    const sorted = useMemo(() => {
        return [...rows].sort((a, b) => {
            if (a.enabled !== b.enabled) return a.enabled ? -1 : 1
            return (a.email || '').localeCompare(b.email || '')
        })
    }, [rows])

    const dirtyCount = Object.keys(drafts).length

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <div>
                    <h4 className='mb-0'>Notification email recipients</h4>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                        One shared list drives every admin notification. Tick a column to opt an
                        address in to that type; untick to opt out. Save applies per row.
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={fetch}
                        disabled={loading || saving}
                        variant='outlined'
                        size='small'
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            <Alert severity='info' className='mb-3'>
                Backing store: <code>notification_email</code> table. Each row can subscribe to any
                combination of <strong>self-register</strong>, <strong>plan-change</strong>, and
                <strong> data-source ingest digests</strong>. Disabling a row silences it across
                all types without deleting the entry — the audit trail is preserved.
            </Alert>

            {loading && !rows.length && (
                <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
            )}

            {/* Add-new form */}
            <Paper variant='outlined' style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Add a new recipient</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <TextField
                        size='small'
                        variant='outlined'
                        label='Email'
                        type='email'
                        value={newDraft.email}
                        onChange={(e) => setNewDraft({ ...newDraft, email: e.target.value })}
                        style={{ minWidth: 280 }}
                        disabled={saving}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Checkbox
                            size='small'
                            checked={newDraft.notifyOnRegistration}
                            onChange={(e) => setNewDraft({ ...newDraft, notifyOnRegistration: e.target.checked })}
                            disabled={saving}
                        />
                        Sign-ups
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Checkbox
                            size='small'
                            checked={newDraft.notifyOnPlanChange}
                            onChange={(e) => setNewDraft({ ...newDraft, notifyOnPlanChange: e.target.checked })}
                            disabled={saving}
                        />
                        Plan changes
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        <Checkbox
                            size='small'
                            checked={newDraft.notifyOnDsIngest}
                            onChange={(e) => setNewDraft({ ...newDraft, notifyOnDsIngest: e.target.checked })}
                            disabled={saving}
                        />
                        Data-source ingest
                    </label>
                    <Button
                        startIcon={<AddIcon />}
                        variant='contained'
                        color='primary'
                        size='small'
                        disabled={saving || !newDraft.email}
                        onClick={addNew}
                    >
                        Add
                    </Button>
                </div>
            </Paper>

            {/* Existing rows */}
            <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 600 }}>Email</TableCell>
                            <TableCell align='center' style={{ fontWeight: 600, width: 90 }}>
                                <Tooltip title='Master switch — off silences every notification for this recipient'><span>Enabled</span></Tooltip>
                            </TableCell>
                            <TableCell align='center' style={{ fontWeight: 600, width: 110 }}>
                                <Tooltip title='New customer sign-up'><span>Sign-ups</span></Tooltip>
                            </TableCell>
                            <TableCell align='center' style={{ fontWeight: 600, width: 130 }}>
                                <Tooltip title='Stripe plan change events'><span>Plan changes</span></Tooltip>
                            </TableCell>
                            <TableCell align='center' style={{ fontWeight: 600, width: 170 }}>
                                <Tooltip title='Daily digest of failed / stale data-source ingests (State Medicaid today; LEIE / NPPES / SAM.gov as they subscribe)'>
                                    <span>Data-source ingest</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align='right' style={{ fontWeight: 600, width: 90 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((row) => {
                            const cur = currentFor(row)
                            const dirty = drafts[row.email] != null
                            return (
                                <TableRow key={row.email} style={{ background: dirty ? '#fff8e1' : undefined }}>
                                    <TableCell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span>{row.email}</span>
                                            {dirty && (
                                                <span style={{ fontSize: 11, color: 'var(--badge-watch-ink)', letterSpacing: '.05em', textTransform: 'uppercase' }}>
                                                    unsaved
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Checkbox
                                            size='small'
                                            checked={!!cur.enabled}
                                            onChange={(e) => setDraftField(row.email, 'enabled', e.target.checked)}
                                            disabled={saving}
                                        />
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Checkbox
                                            size='small'
                                            checked={!!cur.notifyOnSelfRegister}
                                            onChange={(e) => setDraftField(row.email, 'notifyOnSelfRegister', e.target.checked)}
                                            disabled={saving}
                                        />
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Checkbox
                                            size='small'
                                            checked={!!cur.notifyOnPlanChange}
                                            onChange={(e) => setDraftField(row.email, 'notifyOnPlanChange', e.target.checked)}
                                            disabled={saving}
                                        />
                                    </TableCell>
                                    <TableCell align='center'>
                                        <Checkbox
                                            size='small'
                                            checked={!!cur.notifyOnDsIngest}
                                            onChange={(e) => setDraftField(row.email, 'notifyOnDsIngest', e.target.checked)}
                                            disabled={saving}
                                        />
                                    </TableCell>
                                    <TableCell align='right'>
                                        <Tooltip title={dirty ? 'Save changes' : 'No changes'}>
                                            <span>
                                                <IconButton
                                                    size='small'
                                                    color='primary'
                                                    onClick={() => saveRow(row.email)}
                                                    disabled={!dirty || saving}
                                                >
                                                    <SaveIcon fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#666' }}>
                                    No recipients yet. Add one above.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {dirtyCount > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--badge-watch-ink)' }}>
                    {dirtyCount} row{dirtyCount === 1 ? '' : 's'} with unsaved changes — click the save icon on each row to apply.
                </div>
            )}
        </>
    )
}

export default NotificationEmails
